import * as React from 'react';
import {faCheckCircle, faExclamationCircle, faExclamationTriangle} from '@fortawesome/pro-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {ForwardedRef, forwardRef} from 'react';

export type Variant = 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' | 'dark' | 'light';

export interface AlertsProps {
    alertLinkHidden?: boolean;
    alertLinkHref?: string;
    alertLinkText?: string;
    className?: string;
    CssStyles?: React.CSSProperties;
    e2e?: string;
    title?: string | React.JSX.Element;
    message: string | React.JSX.Element;
    message2?: string | React.JSX.Element;
    multiLine?: boolean;
    onClickAlertLink?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
    variant: Variant;
}

const variantStyles: Record<Variant, any> = {
    primary: {
        alert: 'tw-bg-[#ccdeeb] tw-border tw-border-[#b8d1e3] tw-text-[#002e51] [&_a]:!tw-text-[#00111e]',
    },
    secondary: {
        alert: 'tw-bg-gray-300 tw-border tw-border-gray-350 tw-text-gray-600 [&_a]:!tw-text-[#090909]',
    },
    success: {
        alert: 'tw-bg-[#cce6cc] tw-border tw-border-[#b8dbb8] tw-text-[#004300] [&_a]:!tw-text-[#001000]',
    },
    danger: {
        alert: 'tw-bg-[#f4cfd5] tw-border tw-border-[#f0bcc4] tw-text-[#680818] [&_a]:tw-text-[#39040d]',
    },
    warning: {
        alert: 'tw-bg-[#ffebd6] tw-border !tw-border-[#ffe2c6] tw-text-[#85501b] [&_a]:!tw-text-[#5b3712]',
    },
    info: {
        alert: 'tw-bg-[#ccdeeb] tw-border tw-border-[#b8d1e3] tw-text-[#002e51] [&_a]:!tw-text-[#00111e]',
    },
    dark: {
        alert: 'tw-bg-gray-300 tw-border tw-border-gray-350 tw-text-gray-600 [&_a]:!tw-text-[#040505]',
    },
    light: {
        alert: 'tw-bg-gray-100 tw-border tw-border-gray-200 tw-text-gray-400 [&_a]:!tw-text-[#565656]',
    },
};

export const Alerts = forwardRef<HTMLDivElement, AlertsProps>(
    (
        {
            alertLinkHidden,
            alertLinkHref,
            alertLinkText,
            className,
            CssStyles,
            e2e,
            title,
            message,
            message2,
            multiLine,
            onClickAlertLink,
            variant,
        }: AlertsProps,
        ref: ForwardedRef<HTMLDivElement>,
    ) => {
        const {alert} = variantStyles[variant];

        const alertStyles = [alert];

        /**
         * Returns FontAwesomeIcon based on iconVariant
         * @param iconVariant
         */
        const getIconType = (iconVariant: Variant): JSX.Element => {
            switch (iconVariant) {
                case 'danger':
                    return (
                        <FontAwesomeIcon
                            className="tw-mr-4"
                            icon={faExclamationCircle}
                        />
                    );
                case 'warning':
                    return (
                        <FontAwesomeIcon
                            className="tw-mr-4"
                            icon={faExclamationTriangle}
                        />
                    );
                case 'success':
                    return (
                        <FontAwesomeIcon
                            className="tw-mr-4"
                            data-e2e={e2e}
                            icon={faCheckCircle}
                        />
                    );
                default:
                    return;
            }
        };

        /**
         * Returns calculated className
         */
        const formatClassStyles = (): string => '' + (className ? className + ' ' : '') + (multiLine ? 'alert-multiline' : '');

        /**
         * Template
         */
        return message ? (
            <div
                className={`tw-relative tw-mb-4 tw-py-3 tw-px-5 tw-border tw-rounded-sm tw-items-center tw-flex ${formatClassStyles()} ${alertStyles}`}
                data-e2e={e2e}
                ref={ref}
                style={CssStyles}
            >
                {getIconType(variant)}
                <div className="tw-whitespace-normal">
                    {title && <div className="tw-font-bold">{title}</div>}
                    {message}{' '}
                    {!alertLinkHidden && (alertLinkHref || onClickAlertLink) && (
                        <a
                            className="tw-font-bold tw-cursor-pointer"
                            href={alertLinkHref}
                            onClick={(e) => {
                                if (onClickAlertLink) {
                                    e.preventDefault();
                                    onClickAlertLink(e);
                                }
                            }}
                        >
                            {alertLinkText}
                        </a>
                    )}
                    {message2 && <div>{message2}</div>}
                </div>
            </div>
        ) : null;
    },
);
